import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useBulkDeleteProjects,
  useDeleteProject,
  useInvalidateProjectsList,
  useInvalidateRunsList,
  useMergeProjects,
  useProjectsList,
  useRunsList,
  type AgentRun,
  type Project,
} from "../api";

import { RouteBadge } from "../components/RouteBadge";
import { VerdictPill } from "../components/VerdictPill";
import { extractHeadline, formatRelative } from "../utils/formatRelative";
import styles from "./OperationsRoute.module.css";

interface ProjectGroup {
  project_id: string;
  project: Project | undefined;
  runs: AgentRun[];
  latestCreatedAt: string;
}

const groupProjects = (
  projects: Project[],
  runs: AgentRun[],
): ProjectGroup[] => {
  const byProject: Record<string, AgentRun[]> = {};
  for (const r of runs) {
    (byProject[r.project_id] ??= []).push(r);
  }
  const projectMeta: Record<string, Project> = {};
  for (const p of projects) {
    projectMeta[p.project_id] = p;
    byProject[p.project_id] ??= [];
  }
  return Object.entries(byProject)
    .map(([project_id, runsForProject]) => ({
      project_id,
      project: projectMeta[project_id],
      runs: runsForProject,
      latestCreatedAt: runsForProject[0]?.created_at ?? "",
    }))
    .sort((a, b) => {
      if (a.latestCreatedAt && b.latestCreatedAt) {
        return b.latestCreatedAt.localeCompare(a.latestCreatedAt);
      }
      if (a.latestCreatedAt && !b.latestCreatedAt) return -1;
      if (!a.latestCreatedAt && b.latestCreatedAt) return 1;
      return a.project_id.localeCompare(b.project_id);
    });
};

export const OperationsRoute = () => {
  const runsQuery = useRunsList();
  const projectsQuery = useProjectsList();
  const invalidateRuns = useInvalidateRunsList();
  const invalidateProjects = useInvalidateProjectsList();
  const deleteProject = useDeleteProject();
  const bulkDeleteProjects = useBulkDeleteProjects();
  const mergeProjects = useMergeProjects();

  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const groups = useMemo(
    () =>
      groupProjects(projectsQuery.data ?? [], runsQuery.data ?? []),
    [projectsQuery.data, runsQuery.data],
  );

  const loading = runsQuery.isLoading || projectsQuery.isLoading;
  // `isFetching` covers both initial loads and background refetches
  // triggered by `invalidateQueries`. Watching it (rather than just
  // `isLoading`) means the Refresh button greys out + shows
  // "Refreshing…" for every click, not only the very first page load.
  // Without it the operator gets zero visual feedback on a refetch
  // and concludes the button is dead.
  const fetching = runsQuery.isFetching || projectsQuery.isFetching;
  const err =
    runsQuery.error || projectsQuery.error || bulkDeleteProjects.error;

  // Stamp the last successful refresh so we can render a "Last
  // refreshed at HH:MM:SS" hint next to the button — gives the
  // operator confirmation the click registered even when the data
  // returned is the same empty list.
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  useEffect(() => {
    if (!fetching && (runsQuery.isSuccess || projectsQuery.isSuccess)) {
      setLastRefreshedAt(new Date());
    }
  }, [fetching, runsQuery.isSuccess, projectsQuery.isSuccess]);

  const refresh = () => {
    invalidateRuns();
    invalidateProjects();
  };

  const toggleOne = (pid: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });

  const allSelected = groups.length > 0 && groups.every((g) => selected.has(g.project_id));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(groups.map((g) => g.project_id)));
  const clearSelection = () => setSelected(new Set());

  const totalProjects = groups.length;
  const totalRuns = groups.reduce((sum, g) => sum + g.runs.length, 0);
  const routedToGrid = (runsQuery.data ?? []).filter(
    (r) => r.triage?.chosen_mode === "grid_review",
  ).length;
  const routedToEngineer = (runsQuery.data ?? []).filter(
    (r) => r.triage?.chosen_mode === "predevelopment",
  ).length;

  const onBulkDelete = async () => {
    const ids = groups.map((g) => g.project_id).filter((id) => selected.has(id));
    if (ids.length === 0) return;
    const totalRunsBeingDeleted = ids.reduce(
      (sum, id) =>
        sum + (groups.find((g) => g.project_id === id)?.runs.length ?? 0),
      0,
    );
    const msg =
      `Delete ${ids.length} project${ids.length === 1 ? "" : "s"}` +
      (totalRunsBeingDeleted > 0
        ? ` and ${totalRunsBeingDeleted} associated run${totalRunsBeingDeleted === 1 ? "" : "s"}`
        : "") +
      `?\n\n${ids.join("\n")}\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    await bulkDeleteProjects.mutateAsync(ids);
    clearSelection();
  };

  const onDelete = async (pid: string, runCount: number) => {
    const msg = runCount
      ? `Delete project "${pid}" and its ${runCount} run${runCount === 1 ? "" : "s"}? This cannot be undone.`
      : `Delete project "${pid}"? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    await deleteProject.mutateAsync(pid);
  };

  const onMerge = async (pid: string) => {
    const candidates = groups
      .map((g) => g.project_id)
      .filter((id) => id !== pid);
    if (candidates.length === 0) {
      window.alert("No other projects to merge into.");
      return;
    }
    const target = window.prompt(
      `Merge "${pid}" into which project?\n\n` +
        `Existing projects:\n${candidates.join("\n")}\n\n` +
        `Type the target project ID exactly:`,
      "",
    );
    if (!target || target.trim() === "" || target.trim() === pid) return;
    if (
      !window.confirm(
        `Merge "${pid}" into "${target.trim()}"?\n\n` +
          `All runs / cost approvals / project state under "${pid}" will be relocated. "${pid}" will be removed from Operations. This cannot be undone.`,
      )
    )
      return;
    await mergeProjects.mutateAsync({
      from_project_id: pid,
      to_project_id: target.trim(),
    });
  };

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <div>
          <h2 className={styles.h2}>Operations</h2>
          <p className={styles.lede}>
            The whole portfolio in one view — every project the platform touches, across
            the full lifecycle. Click a project to expand its history.
          </p>
        </div>
        <div className={styles.refreshGroup}>
          {lastRefreshedAt && !fetching && (
            <span className={styles.lastRefreshed}>
              Last refreshed {lastRefreshedAt.toLocaleTimeString()}
            </span>
          )}
          <button
            className={styles.refreshBtn}
            onClick={refresh}
            disabled={fetching}
          >
            {fetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {err ? (
        <div className={styles.err}>
          Couldn't load Operations: {(err as Error).message}
        </div>
      ) : null}

      {!err && !loading && groups.length === 0 ? (
        <div className={styles.empty}>
          <strong>No projects yet.</strong>
          <p>
            Submit a project from the <Link to="/new">New project</Link> page to see
            it land here.
          </p>
          <p className={styles.emptyHint}>
            Just submitted one? Atlas typically takes 1-3 minutes per project (longer
            for big document bundles). The project + run record only appear after Atlas
            finishes — click Refresh in a minute.
          </p>
        </div>
      ) : null}

      {groups.length > 0 ? (
        <>
          <div className={styles.summary}>
            <SummaryTile label="Projects" value={totalProjects} />
            <SummaryTile label="Total runs" value={totalRuns} />
            <SummaryTile label="Routed to Grid" value={routedToGrid} />
            <SummaryTile label="Routed to Engineer" value={routedToEngineer} />
          </div>

          <div className={styles.lifecycle}>
            {[
              { id: "predev", label: "Pre-development" },
              { id: "construction", label: "Construction" },
              { id: "operation", label: "Operation" },
              { id: "decommissioning", label: "Decommissioning" },
            ].map((stage) => {
              const planned = stage.id !== "predev";
              const count = planned ? "—" : totalProjects;
              return (
                <div
                  key={stage.id}
                  className={`${styles.lifecycleStage} ${
                    planned ? styles.lifecycleStagePlanned : ""
                  }`}
                >
                  <div className={styles.lifecycleCount}>{count}</div>
                  <div className={styles.lifecycleLabel}>{stage.label}</div>
                  {planned ? (
                    <div className={styles.lifecyclePlannedTag}>Phase 2</div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className={styles.bulkBar}>
            <label className={styles.selectAllLabel}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={toggleAll}
              />
              {selected.size === 0
                ? `Select all (${totalProjects})`
                : `${selected.size} of ${totalProjects} selected`}
            </label>
            {selected.size > 0 ? (
              <div className={styles.bulkActions}>
                <button className={styles.clearBtn} onClick={clearSelection}>
                  Clear
                </button>
                <button
                  className={styles.bulkDeleteBtn}
                  onClick={onBulkDelete}
                  disabled={bulkDeleteProjects.isLoading}
                >
                  {bulkDeleteProjects.isLoading
                    ? `Deleting ${selected.size}…`
                    : `Delete ${selected.size} project${selected.size === 1 ? "" : "s"}`}
                </button>
              </div>
            ) : null}
          </div>

          {groups.map((g) => (
            <ProjectGroupRow
              key={g.project_id}
              group={g}
              selected={selected.has(g.project_id)}
              onToggleSelect={() => toggleOne(g.project_id)}
              onMerge={() => onMerge(g.project_id)}
              onDelete={() => onDelete(g.project_id, g.runs.length)}
              isDeleting={
                deleteProject.isLoading &&
                deleteProject.variables === g.project_id
              }
            />
          ))}
        </>
      ) : null}
    </section>
  );
};

const SummaryTile = ({ label, value }: { label: string; value: number }) => (
  <div className={styles.metric}>
    <div className={styles.metricVal}>{value}</div>
    <div className={styles.metricLabel}>{label}</div>
  </div>
);

const ProjectGroupRow = ({
  group,
  selected,
  onToggleSelect,
  onMerge,
  onDelete,
  isDeleting,
}: {
  group: ProjectGroup;
  selected: boolean;
  onToggleSelect: () => void;
  onMerge: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}) => {
  const [expanded, setExpanded] = useState(group.runs.length > 0);
  const latest = group.runs[0];
  const queued = group.runs.length === 0;
  return (
    <div
      className={`${styles.group} ${selected ? styles.groupSelected : ""}`}
    >
      <div className={styles.groupHead}>
        <input
          type="checkbox"
          className={styles.rowCheckbox}
          checked={selected}
          onChange={onToggleSelect}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${group.project_id}`}
        />
        <button
          type="button"
          className={styles.groupHeadLeft}
          onClick={() => setExpanded((s) => !s)}
        >
          <div className={styles.projectId}>{group.project_id}</div>
          <div className={styles.projectMeta}>
            {queued
              ? `Queued · no runs yet${
                  group.project?.address ? ` · ${group.project.address}` : ""
                }`
              : `${group.runs.length} run${
                  group.runs.length === 1 ? "" : "s"
                } · latest ${formatRelative(latest?.created_at)}`}
          </div>
        </button>
        <div className={styles.groupRight}>
          {queued ? (
            <span className={styles.queuedPill}>Queued</span>
          ) : (
            <>
              <RouteBadge mode={latest?.triage?.chosen_mode} />
              <VerdictPill verdict={latest?.decision?.verdict} />
            </>
          )}
          <Link
            to={`/new?project=${encodeURIComponent(group.project_id)}`}
            className={styles.openBtn}
            onClick={(e) => e.stopPropagation()}
            title="Open this project to add documents / re-run"
          >
            Open
          </Link>
          <Link
            to={`/projects/${encodeURIComponent(group.project_id)}`}
            className={styles.editBtn}
            onClick={(e) => e.stopPropagation()}
            title="Edit this project's tracker fields + status notes (does not run any agent)"
          >
            Edit
          </Link>
          <button
            type="button"
            className={styles.mergeBtn}
            onClick={(e) => {
              e.stopPropagation();
              onMerge();
            }}
            title="Merge this project into another"
          >
            Merge
          </button>
          <button
            type="button"
            className={styles.deleteBtn}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            disabled={isDeleting}
            title="Delete project + all runs"
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </button>
          <button
            type="button"
            className={styles.expandBtn}
            onClick={() => setExpanded((s) => !s)}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? "−" : "+"}
          </button>
        </div>
      </div>
      {expanded && !queued ? (
        <table className={styles.runsTable}>
          <thead>
            <tr>
              <th className={styles.th}>When</th>
              <th className={styles.th}>Routed to</th>
              <th className={styles.th}>Action</th>
              <th className={styles.th}>Verdict</th>
              <th className={styles.th}>Confidence</th>
              <th className={styles.th}>Files</th>
            </tr>
          </thead>
          <tbody>
            {group.runs.map((r) => (
              <tr key={r.run_id} className={styles.runRow}>
                <td className={styles.td}>
                  <Link
                    to={`/runs/${encodeURIComponent(r.project_id)}/${encodeURIComponent(r.run_id)}`}
                    className={styles.runLink}
                  >
                    {formatRelative(r.created_at)}
                  </Link>
                </td>
                <td className={styles.td}>
                  <RouteBadge mode={r.triage?.chosen_mode} />
                </td>
                <td className={styles.td}>
                  {extractHeadline(r.decision?.action)}
                </td>
                <td className={styles.td}>
                  <VerdictPill verdict={r.decision?.verdict} />
                </td>
                <td className={styles.td}>
                  {r.decision?.confidence != null
                    ? `${(r.decision.confidence * 100).toFixed(0)}%`
                    : "—"}
                </td>
                <td className={styles.td}>{r.file_refs?.length ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
};
